import { useState, useRef, type KeyboardEvent } from "react";

interface Props {
  open: boolean;
  onSubmit: (code: string) => void;
  onClose: () => void;
  loading?: boolean;
}

export default function TotpModal({ open, onSubmit, onClose, loading }: Props) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(""));
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  if (!open) return null;

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...digits];
    newDigits[index] = value.slice(-1);
    setDigits(newDigits);

    // Auto-advance
    if (value && index < 5) {
      refs.current[index + 1]?.focus();
    }

    // Auto-submit when all filled
    if (newDigits.every((d) => d !== "")) {
      onSubmit(newDigits.join(""));
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{ width: 340, textAlign: "center" }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginBottom: 8, fontSize: 16, fontWeight: 600 }}>
          Enter TOTP
        </h3>
        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>
          Enter the 6-digit code from your authenticator app
        </p>

        <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 16 }}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              style={{
                width: 42,
                height: 48,
                textAlign: "center",
                fontSize: 20,
                fontFamily: "var(--font-mono)",
                fontWeight: 600,
                borderRadius: 6,
              }}
            />
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn-ghost"
            onClick={onClose}
            style={{ flex: 1 }}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={() => onSubmit(digits.join(""))}
            disabled={loading || digits.some((d) => !d)}
            style={{ flex: 1 }}
          >
            {loading ? "Verifying…" : "Verify"}
          </button>
        </div>
      </div>
    </div>
  );
}
